import {
  type KilnCoefficientsResponse,
  type MtmYtmRequestPayload,
  type KpiPfyPayload,
  type PlannedYearlyKilnCoefficientHierarchy,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useQueries, UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback, useMemo} from 'react'

import {mapKilnCoefficientsDto} from '../../mappers'
import {type KpiHookReturn} from '../../types'

import {getKilnCoefficientMtmYtmQueryKeys, getKilnCoefficients} from './useKilnCoefficients'

const getKilnCoefficientsPfy = async (payload?: KpiPfyPayload, signal?: AbortSignal) => {
  const url = '/kpi/dashboard/kiln-coefficients-pfy'
  const response = await ApiClient.axiosInstance.get<PlannedYearlyKilnCoefficientHierarchy>(url, {
    params: {...payload},
    signal
  })

  return response.data
}

export const useKilnCoefficientsData = (
  payload: MtmYtmRequestPayload,
  enabled = true
): KpiHookReturn => {
  const upmPlants = useUpmPlantsContext()

  const queries = useQueries<
    [
      UseQueryResult<PlannedYearlyKilnCoefficientHierarchy, AxiosError>,
      UseQueryResult<KilnCoefficientsResponse, AxiosError>
    ]
  >({
    queries: [
      {
        queryKey: ['kiln-coefficients-pfy', {locationId: payload?.locationId, year: payload?.year}],
        queryFn: ({signal}) =>
          getKilnCoefficientsPfy(
            {
              locationId: payload?.locationId,
              year: payload?.year
            },
            signal
          ),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      },
      {
        queryKey: getKilnCoefficientMtmYtmQueryKeys(payload),
        queryFn: ({signal}) => getKilnCoefficients(payload, signal),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      }
    ]
  })

  const refetch = useCallback(() => {
    queries.forEach(({error, refetch: queryRefetch}) => {
      if (error) {
        void queryRefetch()
      }
    })
  }, [queries])

  const [pfyQuery, dataQuery] = queries

  const kilnCoefficients = useMemo(() => {
    const pfyData = pfyQuery.data
    const data = dataQuery.data
    return pfyData && data ? mapKilnCoefficientsDto(data, upmPlants, pfyData) : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pfyQuery.data), JSON.stringify(dataQuery.data)])

  return {
    data: kilnCoefficients,
    isInitialLoading: pfyQuery.isInitialLoading || dataQuery.isInitialLoading,
    error: pfyQuery.error || dataQuery.error,
    refetch
  }
}
